import { Container } from "@material-ui/core";
import { Add, ArrowForward } from "@material-ui/icons";
import { Box, Divider, Grid } from "@mui/material";
import axios from "axios";
import {
  BalanceCard,
  ButtonGroupTop,
  CurrencyDisplay,
  CustomTable,
  ModalError,
  Page,
  PrimaryButton,
  PrimaryTextButton,
  SearchTextInput,
  SectionLabel,
  SelectInput,
  StatusChip,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { formatDate, getErrors, textCapitalization } from "utils";
import { optionsStatusComposerPayment } from "../../Publisher/Invoice/Components/ConstantInvoice";
import ModalFormPayment from "./Components/ModalFormPayment";
import ModalRevenueInformation from "./Components/ModalRevenueInformation";

const PaymentPencipta = ({ userLogin }) => {
  const classes = globalStyles();
  const history = useHistory();
  const isFirstLoad = useRef(true);
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    status: "",
    search: "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [balance, setBalance] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalInformationVisible, setModalInformationVisible] = useState(false);
  const allowRequestPayment =
    userLogin?.publisher?.publisher_config?.allow_composer_request_payment;

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const getPaymentBalance = async () => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/composer/transaction/v2/balance`,
        { headers }
      );
      setBalance(res?.data?.data?.balance);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/composer/transaction/v2/payment`,
        { headers, params: queryParams }
      );
      const { data, meta } = res?.data;
      setDataTable(data || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const fetchData = async () => {
    setLoadingPage(true);
    const promises = [getPaymentBalance(), getDataTable()];

    try {
      await Promise.all(promises);
    } catch (error) {
      ModalError(error, "Error fetching data");
    } finally {
      setLoadingPage(false);
      isFirstLoad.current = false;
    }
  };

  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    if (!isFirstLoad.current) {
      getDataTable();
    }
  }, [queryParams]);

  return (
    <Page className={classes.root} title="Payment">
      <Container maxWidth={false}>
        <HeaderTitle title="Transaction" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes?.flatDivider} />
        <SectionLabel title="Payment Transaction" my="16px" />
        <BalanceCard
          loading={loadingPage}
          title="Composer Balance"
          value={balance}
          infoButton
          tooltipTitle="View Revenue Information"
          onClick={() => setModalInformationVisible(true)}
          subTitle={<Box color="red">* Excluding Taxes</Box>}
        />
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          my="16px"
        >
          <PrimaryTextButton
            label="See Balance Mutation"
            onClick={() =>
              history.push({ pathname: "/pencipta/payment-mutation" })
            }
            endIcon={<ArrowForward />}
            disabled={loadingPage}
          />
          <Grid
            container
            columnSpacing={1}
            alignItems="center"
            justifyContent="space-between"
            my="16px"
          >
            <Grid item xs={6}>
              <SearchTextInput
                placeholder="Search"
                value={queryParams?.search}
                onChange={e =>
                  handleChangeQueryParams(e?.target?.value, "search")
                }
                disabled={loadingPage}
              />
            </Grid>
            <Grid item xs={6}>
              <Grid container spacing={1} justifyContent="flex-end">
                <Grid item>
                  <SelectInput
                    label="Status"
                    placeholder="None"
                    options={optionsStatusComposerPayment}
                    optionKey="value"
                    optionLabel="name"
                    value={queryParams?.status || ""}
                    onChange={e =>
                      handleChangeQueryParams(e?.target?.value, "status")
                    }
                    width={200}
                    disabled={loadingPage}
                  />
                </Grid>
                {allowRequestPayment && (
                  <Grid item>
                    <PrimaryButton
                      label="Request Payment"
                      onClick={() => setModalVisible(true)}
                      startIcon={<Add />}
                      size="large"
                      disabled={loadingPage}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.size}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "size")
          }
          isHaveAction
          handleView={item => history.push(`/pencipta/payment/${item?.id}`)}
          tooltipView="View"
        />
      </Container>
      {modalVisible && (
        <ModalFormPayment
          open={modalVisible}
          onClose={() => setModalVisible(false)}
          getDataTable={getDataTable}
          userLogin={userLogin}
        />
      )}
      {modalInformationVisible && (
        <ModalRevenueInformation
          open={modalInformationVisible}
          onClose={() => setModalInformationVisible(false)}
        />
      )}
    </Page>
  );
};

const breadcrumbData = [
  {
    label: "Home",
    link: "/pencipta/dashboard-pencipta",
  },
  {
    label: "Transaction",
    link: "/pencipta/payment",
  },
  {
    label: "Payment",
    active: true,
  },
];
const columnTable = [
  {
    name: "invoice_date",
    title: "Date",
    renderText: item => item && formatDate(item),
  },
  {
    name: "invoice_number",
    title: "Invoice Number",
  },
  {
    name: "payment_number",
    title: "Payment Number",
  },
  {
    name: "publisher_name",
    title: "Publisher",
  },
  {
    name: "invoice_value",
    title: <Box textAlign="right">Value</Box>,
    renderText: item => <CurrencyDisplay value={item} prefix="Rp" />,
  },
  {
    name: "status",
    title: "Status",
    renderText: status => {
      const selectedStatus = optionsStatusComposerPayment?.find(
        item => item?.value === status
      );
      return (
        <StatusChip
          label={selectedStatus?.name || textCapitalization(status) || "-"}
          type={selectedStatus?.color}
        />
      );
    },
  },
];
export default PaymentPencipta;
